import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, firstValueFrom, of } from "rxjs";
import { ApiService } from "../core/api.service";
import { ConfirmDialogService } from "../core/confirm-dialog.service";
import { resizeCatalogImage } from "../core/image.util";
import { BlindCategory, CatalogBlind } from "../core/models";

const NORMALIZE_TAGS = (raw: string): string[] =>
  [
    ...new Set(
      raw
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];

@Component({
  selector: "app-blind-list-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterModule, MatIconModule],
  template: `
    <section class="dashboard">
      <div class="dashboard-main">
        <header class="dashboard-header">
          <div>
            <h1>Blinds</h1>
            <p class="header-subtitle">Codes, images, and searchable tags.</p>
          </div>
          <div class="header-actions">
            <a class="action-button" routerLink="/catalog">← Back</a>
          </div>
        </header>

        <div class="panel-card">
          <div class="panel-header">
            <h2>Add blind</h2>
          </div>
          <div class="panel-content">
            <div class="blind-form">
              <div class="form-grid">
                <div class="form-field-group">
                  <label>Name</label>
                  <input class="form-input" [(ngModel)]="blindForm.name" placeholder="e.g. Ribbon" />
                </div>
                <div class="form-field-group">
                  <label>Semi-blackout code</label>
                  <input class="form-input" [(ngModel)]="blindForm.semiBlackoutCode" placeholder="e.g. RB-100S" />
                </div>
                <div class="form-field-group">
                  <label>Blackout code</label>
                  <input class="form-input" [(ngModel)]="blindForm.blackoutCode" placeholder="e.g. RB-100B" />
                </div>
                <div class="form-field-group">
                  <label>Category</label>
                  <select class="form-select" [(ngModel)]="blindForm.categoryId">
                    <option value="">-- Select category --</option>
                    @for (cat of categories(); track cat.id) {
                      <option [value]="cat.id">{{ cat.name }}</option>
                    }
                  </select>
                </div>
                <div class="form-field-group">
                  <label>Tags</label>
                  <input
                    class="form-input"
                    [(ngModel)]="blindForm.tags"
                    placeholder="e.g. beige, warm tones, linen"
                  />
                  <span class="field-hint">Comma-separated · used for customer search</span>
                </div>
                <div class="form-field-group">
                  <label>Supplier</label>
                  <select class="form-select" [(ngModel)]="blindForm.supplierId">
                    <option value="">-- Select supplier --</option>
                    @for (s of suppliers(); track s.id) {
                      <option [value]="s.id">{{ s.name }}</option>
                    }
                  </select>
                  <span class="field-hint">Required before the blind shows to customers</span>
                </div>
                <div class="form-field-group checkbox-field">
                  <label class="checkbox-label">
                    <input type="checkbox" [(ngModel)]="blindForm.favorite" />
                    Featured on home page
                  </label>
                </div>
                <div class="form-field-group checkbox-field">
                  <label class="checkbox-label">
                    <input type="checkbox" [(ngModel)]="blindForm.visible" />
                    Visible to customers
                  </label>
                </div>
              </div>
              <div class="form-field-group image-field">
                <label>Image</label>
                <div
                  class="image-dropzone"
                  [class.dragging]="isDragging()"
                  (click)="imageInput.click()"
                  (dragover)="onDragOver($event)"
                  (dragleave)="onDragLeave()"
                  (drop)="onDrop($event)"
                >
                  <input
                    #imageInput
                    type="file"
                    accept="image/*"
                    hidden
                    (change)="onImageSelected($event)"
                  />
                  @if (imagePreview()) {
                    <img class="dropzone-preview" [src]="imagePreview()" alt="Blind preview" />
                    <div class="dropzone-overlay">
                      <span class="dropzone-overlay-btn">Change</span>
                      <span class="dropzone-overlay-btn danger" (click)="clearImage($event)">Remove</span>
                    </div>
                  } @else {
                    <div class="dropzone-placeholder">
                      <mat-icon class="dropzone-icon">add_photo_alternate</mat-icon>
                      <span class="dropzone-title">Upload blind image</span>
                      <span class="dropzone-hint">Click or drag &amp; drop · JPG or PNG</span>
                    </div>
                  }
                </div>
              </div>
              <div class="actions">
                <button class="action-button primary" (click)="saveBlind()" [disabled]="saving()">
                  {{ saving() ? "Saving..." : "Add blind" }}
                </button>
                <button class="action-button" (click)="cancelBlindForm()">Cancel</button>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header">
            <h2>All blinds</h2>
            <p class="panel-description">{{ blinds()?.length ?? 0 }} total</p>
          </div>
          <div class="panel-content">
            <div class="blinds-grid">
              @for (blind of blinds(); track blind.id) {
                <div class="blind-card" (click)="openBlind(blind)">
                  @if (blind.isFavorite) {
                    <span class="fav-badge" title="Featured on home page">★</span>
                  }
                  @if (blind.imageUrl) {
                    <img class="blind-thumb" [src]="blind.imageUrl" [alt]="blind.name" />
                  } @else {
                    <div class="blind-thumb placeholder">No image</div>
                  }
                  <div class="blind-card-body">
                    <div class="blind-name">{{ blind.name }}</div>
                    <div class="blind-codes">Semi: {{ blind.semiBlackoutCode }} · Blackout: {{ blind.blackoutCode }}</div>
                    <span class="category-badge">{{ blind.categoryName ?? "Uncategorized" }}</span>
                    @if (blind.hidden) {
                      <span class="category-badge warn">Hidden</span>
                    } @else if (!blind.supplierId) {
                      <span class="category-badge warn">No supplier</span>
                    }
                    @if (blind.tags && blind.tags.length > 0) {
                      <div class="tag-row">
                        @for (tag of blind.tags; track tag) {
                          <span class="tag-chip">{{ tag }}</span>
                        }
                      </div>
                    }
                  </div>
                  <div class="blind-card-actions">
                    <button class="chip-btn" (click)="openBlind(blind)" title="Edit">✎</button>
                    <button class="chip-btn danger" (click)="$event.stopPropagation(); deleteBlind(blind.id)" title="Delete">×</button>
                  </div>
                </div>
              } @empty {
                <p class="empty-message">No blinds yet. Add one above.</p>
              }
            </div>
            @if (message()) {
              <div class="status-message">{{ message() }}</div>
            }
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      .dashboard { display: grid; grid-template-columns: 1fr; gap: 24px; align-items: start; }
      .dashboard-main { min-width: 0; }
      .dashboard-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 16px; }
      .dashboard-header h1 { font-size: 32px; margin: 0; }
      .header-subtitle { color: var(--ink-500); margin: 4px 0 0; font-size: 14px; }
      .header-actions { display: flex; gap: 10px; align-items: center; }
      .action-button {
        border: none; background: var(--panel); color: var(--ink-900); padding: 10px 16px;
        border-radius: 12px; font-weight: 600; font-size: 14px; cursor: pointer;
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08); text-decoration: none;
        display: inline-flex; align-items: center;
      }
      .action-button.primary { background: #111827; color: #ffffff; box-shadow: 0 8px 20px rgba(17, 24, 39, 0.15); }
      .action-button:disabled { opacity: 0.5; cursor: not-allowed; }
      .panel-card { background: var(--panel); border-radius: 20px; padding: 24px; box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1); margin-bottom: 24px; }
      .panel-header { margin-bottom: 18px; }
      .panel-header h2 { font-size: 18px; margin: 0; color: var(--ink-900); }
      .panel-description { font-size: 13px; color: var(--ink-500); margin: 4px 0 0; }
      .panel-content { display: grid; gap: 12px; }
      .form-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); margin-bottom: 12px; }
      .form-field-group { display: flex; flex-direction: column; gap: 6px; }
      .form-field-group label { font-size: 13px; font-weight: 600; color: var(--ink-900); }
      .field-hint { font-size: 11px; color: var(--ink-500); }
      .checkbox-field { justify-content: center; }
      .checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: var(--ink-900); cursor: pointer; padding-top: 24px; }
      .checkbox-label input { width: 16px; height: 16px; accent-color: #111827; }
      .form-select, .form-input { padding: 10px 12px; border: 1px solid rgba(15, 23, 42, 0.12); border-radius: 10px; font-size: 14px; font-family: inherit; color: var(--ink-900); background: #fafbfc; }
      .form-select:focus, .form-input:focus { outline: none; border-color: #111827; background: #ffffff; box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.08); }
      .blind-form { border: 1px solid rgba(15,23,42,0.1); border-radius: 12px; padding: 16px; background: #fafbfc; }
      .image-field { margin-top: 4px; }
      .image-dropzone { position: relative; border: 2px dashed rgba(15, 23, 42, 0.18); border-radius: 12px; min-height: 150px; display: flex; align-items: center; justify-content: center; cursor: pointer; overflow: hidden; background: #fafbfc; transition: border-color 0.2s ease, background 0.2s ease; }
      .image-dropzone.dragging, .image-dropzone:hover { border-color: rgba(17, 24, 39, 0.6); background: #ffffff; }
      .dropzone-placeholder { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 24px; text-align: center; font-size: 13px; color: var(--ink-500); }
      .dropzone-icon { font-size: 32px; width: 32px; height: 32px; line-height: 32px; color: var(--ink-500); }
      .dropzone-title { font-weight: 600; color: var(--ink-900); font-size: 14px; }
      .dropzone-hint { font-size: 12px; color: var(--ink-500); opacity: 0.8; }
      .dropzone-preview { width: 100%; height: 150px; object-fit: cover; display: block; }
      .dropzone-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 8px; background: rgba(15, 23, 42, 0.45); opacity: 0; transition: opacity 0.2s ease; }
      .image-dropzone:hover .dropzone-overlay { opacity: 1; }
      .dropzone-overlay-btn { background: #ffffff; color: var(--ink-900); font-size: 12px; font-weight: 600; padding: 7px 14px; border-radius: 10px; cursor: pointer; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18); }
      .dropzone-overlay-btn.danger { color: #dc2626; }
      .actions { display: flex; gap: 8px; margin-top: 12px; }
      .blinds-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px; }
      .blind-card { border: 1px solid rgba(15,23,42,0.1); border-radius: 12px; overflow: hidden; background: #fff; box-shadow: 0 4px 12px rgba(15,23,42,0.06); position: relative; cursor: pointer; }
      .blind-thumb { width: 100%; height: 120px; object-fit: cover; display: block; }
      .blind-thumb.placeholder { display: flex; align-items: center; justify-content: center; background: #f1f5f9; color: var(--ink-500); font-size: 12px; }
      .blind-card-body { padding: 10px 12px; }
      .blind-name { font-weight: 600; font-size: 14px; color: var(--ink-900); }
      .blind-codes { font-size: 12px; color: var(--ink-500); margin-top: 2px; }
      .category-badge { display: inline-block; margin-top: 6px; padding: 2px 8px; border-radius: 999px; background: #eef2ff; color: #3730a3; font-size: 11px; }
      .category-badge.warn { background: #fef3c7; color: #92400e; margin-left: 4px; }
      .tag-row { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
      .tag-chip { display: inline-block; padding: 2px 8px; border-radius: 999px; background: #f0f9f0; color: #166534; font-size: 10px; font-weight: 600; }
      .blind-card-actions { position: absolute; top: 8px; right: 8px; display: flex; gap: 4px; background: rgba(255,255,255,0.9); border-radius: 8px; padding: 2px; }
      .fav-badge { position: absolute; top: 8px; left: 8px; z-index: 2; width: 28px; height: 28px; border-radius: 50%; background: #f59e0b; color: #fff; font-size: 15px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
      .chip-btn { border: none; background: transparent; cursor: pointer; color: var(--ink-500); font-size: 13px; padding: 2px 4px; }
      .chip-btn.danger { color: #dc2626; }
      .chip-btn:hover { background: rgba(15,23,42,0.06); border-radius: 6px; }
      .empty-message { color: var(--ink-500); font-size: 13px; }
      .status-message { color: var(--ink-500); font-size: 13px; padding: 12px; background: #fef3c7; border-radius: 10px; margin-top: 12px; }

    `,
  ],
})
export class BlindListPageComponent {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly saving = signal(false);
  readonly message = signal("");
  readonly isDragging = signal(false);

  blindForm = {
    name: "",
    semiBlackoutCode: "",
    blackoutCode: "",
    categoryId: "",
    tags: "",
    favorite: false,
    supplierId: "",
    visible: true,
  };
  imagePreviewSignal = signal<string | null>(null);
  selectedImage: File | null = null;

  readonly categoriesResource = rxResource({
    stream: () =>
      this.api.getCategories().pipe(catchError(() => of([] as BlindCategory[]))),
  });
  readonly categories = this.categoriesResource.value;

  readonly suppliersResource = rxResource({
    stream: () =>
      this.api
        .listSuppliers()
        .pipe(catchError(() => of([] as { id: string; name: string }[]))),
  });
  readonly suppliers = this.suppliersResource.value;

  readonly blindsResource = rxResource({
    stream: () =>
      this.api.getCatalogBlinds().pipe(catchError(() => of([] as CatalogBlind[]))),
  });
  readonly blinds = this.blindsResource.value;

  imagePreview() {
    return this.imagePreviewSignal();
  }

  startAddBlind() {
    this.blindForm = { name: "", semiBlackoutCode: "", blackoutCode: "", categoryId: "", tags: "", favorite: false, supplierId: "", visible: true };
    this.selectedImage = null;
    this.imagePreviewSignal.set(null);
  }

  cancelBlindForm() {
    this.selectedImage = null;
    this.imagePreviewSignal.set(null);
  }

  openBlind(blind: CatalogBlind) {
    this.router.navigate(["/catalog/blinds", blind.id]);
  }

  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.setImageFile(file);
    input.value = "";
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging.set(true);
  }

  onDragLeave() {
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      this.setImageFile(file);
    } else if (file) {
      this.message.set("Please drop an image file.");
    }
  }

  clearImage(event: Event) {
    event.stopPropagation();
    this.selectedImage = null;
    this.imagePreviewSignal.set(null);
  }

  private setImageFile(file: File) {
    if (!file.type.startsWith("image/")) {
      this.message.set("Please choose an image file.");
      return;
    }
    this.selectedImage = file;
    const reader = new FileReader();
    reader.onload = () => this.imagePreviewSignal.set(reader.result as string);
    reader.readAsDataURL(file);
  }

  async saveBlind() {
    const name = this.blindForm.name.trim();
    const semiBlackoutCode = this.blindForm.semiBlackoutCode.trim();
    const blackoutCode = this.blindForm.blackoutCode.trim();
    if (!name || !semiBlackoutCode || !blackoutCode) {
      this.message.set("Name, semi-blackout code, and blackout code are required.");
      return;
    }
    this.saving.set(true);
    try {
      let imageKey: string | undefined;
      let imageUrl: string | undefined;
      if (this.selectedImage) {
        // Crop to 4:3 and cap at 4K (never upscale) so it fits the customer
        // detail page and zooms cleanly.
        const upload = await resizeCatalogImage(this.selectedImage);
        const presigned = await firstValueFrom(
          this.api.presignCatalogImage(upload.type, upload.name),
        );
        const resp = await fetch(presigned.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": upload.type },
          body: upload,
        });
        if (!resp.ok) throw new Error("Image upload failed");
        imageKey = presigned.objectKey;
        imageUrl = presigned.publicUrl;
      }

      const payload = {
        name,
        semiBlackoutCode,
        blackoutCode,
        categoryId: this.blindForm.categoryId || undefined,
        isFavorite: this.blindForm.favorite,
        tags: NORMALIZE_TAGS(this.blindForm.tags),
        supplierId: this.blindForm.supplierId || undefined,
        hidden: !this.blindForm.visible,
        imageKey,
        imageUrl,
      };

      const created = await firstValueFrom(this.api.createBlind(payload));
      this.blindsResource.update((list) => [...(list ?? []), created]);
      this.message.set("Blind added.");
      this.cancelBlindForm();
    } catch (err: any) {
      this.message.set(err?.message ?? "Failed to save blind.");
    } finally {
      this.saving.set(false);
    }
  }

  async deleteBlind(id: string) {
    const confirmed = await this.confirmDialog.confirm({
      title: "Delete blind",
      message: "Delete this blind?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!confirmed) return;
    this.api.deleteBlind(id).subscribe({
      next: () => {
        this.blindsResource.update((list) =>
          (list ?? []).filter((b) => b.id !== id),
        );
        this.message.set("Blind deleted.");
      },
      error: (err) =>
        this.message.set(err?.error?.message ?? "Failed to delete blind."),
    });
  }

}
